import { Label, Input, DropdownOptions, fieldClassName, labelClassName, errorTextClassName } from '@shared/ui';
import { NO_REGION_SELECTION_LABEL } from '@shared/components/AddressFields/AddressFields';
import { useBillingAddress } from '../../hooks/useBillingAddress';
import { sectionHeading } from '../profileStyles';
import { dropdownErrorClassName } from '@shared/ui/formStyles';

export function BillingAddressSection({ canEditBillingAndContact, register, setValue, watch, errors, isSubmitted = false, dataTestId }) {
  const baseTestId = dataTestId || 'profile-billing-section';
  const {
    countries,
    availableRegions,
    selectedCountry,
    selectedRegion,
    loadingCountries,
    loadingRegions,
    handleCountryChange,
    handleRegionChange,
    noRegionsOption
  } = useBillingAddress({ setValue, watch, isSubmitted });

  return (
    <div className="space-y-4" data-testid={baseTestId}>
        <h3 className={sectionHeading} data-testid={`${baseTestId}-header`}>Billing Details</h3>
      <div className="space-y-2">
        <Label htmlFor="billingEmail" className={labelClassName} data-testid={`${baseTestId}-email-label`}>Billing Email</Label>
        <Input 
          id="billingEmail" 
          {...register('billingEmail')}
          className={`${fieldClassName} ${canEditBillingAndContact ? 'text-fg' : 'text-fg-disabled'} ${errors.billingEmail ? 'border-danger-fg' : ''}`}
          readOnly={!canEditBillingAndContact}
          data-testid={`${baseTestId}-email-input`}
        />
        {errors.billingEmail && (
          <span className={errorTextClassName} data-testid={`${baseTestId}-email-error`}>{errors.billingEmail.message}</span>
        )}
        <p className="text-xs text-fg-muted" data-testid={`${baseTestId}-email-info`}>This email will be used for billing communications in HaloPSA.</p>
      </div>
      <div className="space-y-2">
        <Label htmlFor="address1" className={labelClassName} data-testid={`${baseTestId}-address1-label`}>Address Line 1</Label>
        <Input
          id="address1"
          {...register('address1')}
          placeholder="e.g. Apartment 4B, Suite 200"
          className={`${fieldClassName} ${canEditBillingAndContact ? 'text-fg' : 'text-fg-disabled'} ${errors.address1 ? 'border-danger-fg' : ''}`}
          readOnly={!canEditBillingAndContact}
          data-testid={`${baseTestId}-address1-input`}
        />
        {errors.address1 && (
          <span className={errorTextClassName} data-testid={`${baseTestId}-address1-error`}>{errors.address1.message}</span>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="address2" className={labelClassName} data-testid={`${baseTestId}-address2-label`}>Address Line 2</Label>
        <Input
          id="address2"
          {...register('address2')}
          placeholder="e.g. 123 Main Street (optional)"
          className={`${fieldClassName} ${canEditBillingAndContact ? 'text-fg' : 'text-fg-disabled'} ${errors.address2 ? 'border-danger-fg' : ''}`}
          readOnly={!canEditBillingAndContact}
          data-testid={`${baseTestId}-address2-input`}
        />
        {errors.address2 && (
          <span className={errorTextClassName} data-testid={`${baseTestId}-address2-error`}>{errors.address2.message}</span>
        )}
      </div>
      {/* City | Region/State | Post/ZIP Code, then Country on its own row — the same grid as
          ShippingAddressSection below and the checkout review page (see AddressLines). */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label htmlFor="address3" className={labelClassName} data-testid={`${baseTestId}-address3-label`}>City</Label>
          <Input
            id="address3"
            {...register('address3')}
            placeholder="e.g. Springfield"
            className={`${fieldClassName} ${canEditBillingAndContact ? 'text-fg' : 'text-fg-disabled'} ${errors.address3 ? 'border-danger-fg' : ''}`}
            readOnly={!canEditBillingAndContact}
            data-testid={`${baseTestId}-address3-input`}
          />
          {errors.address3 && (
            <span className={errorTextClassName} data-testid={`${baseTestId}-address3-error`}>{errors.address3.message}</span>
          )}
        </div>

        <DropdownOptions
          id="region"
          label="Region/State"
          value={selectedRegion || ""}
          onValueChange={handleRegionChange}
          options={availableRegions.length === 0 ? [noRegionsOption] : availableRegions}
          loading={loadingRegions}
          disabled={!canEditBillingAndContact || !selectedCountry}
          placeholder={loadingRegions ? "Loading regions..." : "Select region (optional)"}
          placeholderWhenEmpty
          allowEmpty={!loadingRegions && availableRegions.length > 0}
          emptyLabel={NO_REGION_SELECTION_LABEL}
          error={errors.region?.message}
          errorClassName={dropdownErrorClassName}
          dataTestId={`${baseTestId}-region-dropdown`}
        />

        <div className="space-y-2">
          <Label htmlFor="postalCode" className={labelClassName} data-testid={`${baseTestId}-postalcode-label`}>Post/ZIP Code</Label>
          <Input
            id="postalCode"
            {...register('postalCode')}
            className={`${fieldClassName} ${canEditBillingAndContact ? 'text-fg' : 'text-fg-disabled'} ${errors.postalCode ? 'border-danger-fg' : ''}`}
            readOnly={!canEditBillingAndContact}
            data-testid={`${baseTestId}-postalcode-input`}
          />
          {errors.postalCode && (
            <span className={errorTextClassName} data-testid={`${baseTestId}-postalcode-error`}>{errors.postalCode.message}</span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <DropdownOptions
          id="country"
          label="Country"
          value={selectedCountry || ""}
          onValueChange={handleCountryChange}
          options={countries}
          loading={loadingCountries}
          disabled={!canEditBillingAndContact}
          placeholder="Select a country"
          error={errors.country?.message}
          errorClassName={dropdownErrorClassName}
          dataTestId={`${baseTestId}-country-dropdown`}
        />
      </div>
    </div>
  );
}
